<template>
  <div
    class="sm:px-4 lg:px-8"
    :class="{
      'pointer-events-none opacity-70': pending,
    }"
  >
    <div
      class="max-w-screen-lg mx-auto grid md:grid-cols-[max-content_auto] lg:grid-cols-[1fr_3fr] gap-4 pt-4 lg:pt-6"
    >
      <div
        class="col-span-2 lg:text-3xl text-2xl font-semibold hidden md:block"
      >
        Account Settings
      </div>
      <div
        class="left-0 top-0 bottom-0 py-16 bg-neutral-100 dark:bg-black z-20 p-4 md:p-0 md:-ml-2 fixed w-full sm:w-[256px] border-r md:border-none shadow md:shadow-none md:relative transition-all md:transition-none overflow-auto"
        :class="{
          '!-left-full md:!left-0': !showDrawer,
        }"
      >
        <div
          class="col-span-2 md:hidden my-4 flex items-center justify-between"
        >
          <span class="lg:text-3xl text-2xl font-semibold">Settings</span>
          <NamiButton
            button-type="secondary"
            @buttonClick="showDrawer = false"
            :icon="IconX"
            pill
            text
            small
          />
        </div>
        <Drawer
          :items="mappedDrawerPaths"
          class="whitespace-nowrap"
          active-class="!text-nami-nami-blue hover:!text-nami-comi-blue"
          offset-class=""
          @change-nav="showDrawer = false"
        />
      </div>

      <client-only>
        <div class="min-w-0">
          <div class="md:hidden mb-4">
            <NamiButton
              button-type="secondary"
              @buttonClick="showDrawer = true"
              :icon="IconMenu2"
              >Menu</NamiButton
            >
          </div>
          <NuxtPage keepalive @update="update" :isUpdating="isUpdating" />
        </div>
      </client-only>
    </div>
  </div>
  <NamiModalPresets
    v-model="showGoBackToChapterModal"
    preventOutsideClicks
    :options="{
      title: 'Do you want to return to the chapter?',
      icon: 'confused',
      buttons: [
        {
          buttonType: 'secondary',
          buttonText: 'Stay',
          onButtonClick: () => (showGoBackToChapterModal = false),
        },
        {
          buttonType: 'primary',
          buttonText: 'Take me there',
          onButtonClick: () => navigateTo(returnTo),
        },
      ],
    }"
  />
  <NamiNotificationsPresets ref="notifications" />
</template>

<script setup lang="ts">
import NamiNotificationsPresets, {
  type PresetOptions,
} from "~/components/nami/notifications/NamiNotificationsPresets.vue";

import { useCookies } from "@vueuse/integrations/useCookies";
import { IconMenu, IconMenu2, IconMoon, IconX } from "@tabler/icons-vue";
import Drawer from "~/components/Drawer.vue";

const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const settingsStore = nuxtApp.$settings();
const locale = nuxtApp.$i18n.global.locale;
const route = useRoute();
const translate = nuxtApp.$i18n.global.t;

const showGoBackToChapterModal = ref(false);

const isLoggedIn = computed(() => !!authStore?.user);
const returnTo = computed(() => {
  if (!route.query.returnTo) return "";

  return decodeURIComponent(route.query.returnTo as string);
});

const showDrawer = ref(false);
/*
  general
     - public profile
     - site pref (theme, quality, ui language)
     - acc pref (age verif/mature, ch language, i.e. should sync across devices)
     - notif (bell page)
     - privacy (current account page functions)
     - connected apps
   account (outlink to keycloak with appropriate url)
     - email !on keycloak
     - password !on keycloak
         - two factor !same page
     - device management (device activity?)
   subscriptions
     - my history
     - payment info
     - transaction hist
*/

const generalPaths = [
  {
    name: translate("site.settings.drawer.general.profile"),
    value: `/${route.params.locale}/settings/profile`,
  },
  {
    name: translate("site.settings.drawer.general.sitePreferences"),
    value: `/${route.params.locale}/settings/site-preferences`,
  },
  {
    name: translate("site.settings.drawer.general.accountPreferences"),
    value: `/${route.params.locale}/settings/account-preferences`,
  },
  {
    name: translate("site.settings.drawer.general.notifications"),
    value: `/${route.params.locale}/settings/notifications`,
  },
  {
    name: translate("site.settings.drawer.general.privacy"),
    value: `/${route.params.locale}/settings/privacy`,
  },
  {
    name: translate("site.settings.drawer.general.connectedApps"),
    value: `/${route.params.locale}/settings/connected-apps`,
  },
];

const accountPaths = [
  {
    name: translate("site.settings.drawer.account.email"),
    value: `/${route.params.locale}/settings/account/email`,
  },
  {
    name: translate("site.settings.drawer.account.password"),
    value: `/${route.params.locale}/settings/account/password`,
  },
  {
    name: translate("site.settings.drawer.account.twoFactor"),
    value: `/${route.params.locale}/settings/account/two-factor`,
  },
  {
    name: translate("site.settings.drawer.account.deviceManagement"),
    value: `/${route.params.locale}/settings/account/device-management`,
  },
];

const subscriptionPaths = [
  {
    name: translate("site.settings.drawer.subscriptions.history"),
    value: `/${route.params.locale}/settings/subscription/history`,
  },
  {
    name: translate("site.settings.drawer.subscriptions.payment"),
    value: `/${route.params.locale}/settings/subscription/payment`,
  },
  {
    name: translate("site.settings.drawer.subscriptions.transactionHistory"),
    value: `/${route.params.locale}/settings/subscription/transaction-history`,
  },
];

const generalLoggedInOnly = [
  "/profile",
  "/privacy",
  "/notifications",
  "/connected-apps",
];

const mappedDrawerPaths = computed(() =>
  [
    {
      name: "General",
      alwaysOpen: true,
      children: generalPaths
        .map((path) => ({
          name: path.name,
          href: path.value,
        }))
        .filter(
          (path) =>
            isLoggedIn.value ||
            !generalLoggedInOnly.some((slug) => path.href.includes(slug)),
        ), // remove options if not logged in
    },
    isLoggedIn.value && {
      name: "Account",
      alwaysOpen: true,
      children: accountPaths.map((path) => ({
        name: path.name,
        href: path.value,
      })),
    },
    isLoggedIn.value && {
      name: "Subscriptions",
      alwaysOpen: true,
      children: subscriptionPaths.map((path) => ({
        name: path.name,
        href: path.value,
      })),
    },
    !isLoggedIn.value && {
      name: "Account",
      alwaysOpen: true,
      children: [
        {
          name: "Log In",
          action: () =>
            authStore?.login({
              redirectBackTo: route.fullPath,
              locale: locale.value,
            }),
        },
      ],
    },
  ].filter(onlyTruthys),
);

const pending = ref(true);
const cookies = useCookies(["PREF_LANG", "PREF_THEME"]);

const isUpdating = ref(false);
const notifications = ref<InstanceType<typeof NamiNotificationsPresets>>();

const notify = (options: PresetOptions) => {
  if (!notifications.value)
    throw new Error("Notifications component not initialized.");
  return notifications.value.notify(options);
};

const settingsTemplate = computed(
  () => settingsStore?.settings ?? getDefaultSettings().data,
);

async function update() {
  if (!settingsStore)
    throw new Error(
      "Cannot update settings without initializing settings store.",
    );

  isUpdating.value = true;

  const { close } = await notify({
    preset: "loading.plain",
    detail: "Updating settings.",
  });

  const settings = settingsTemplate.value;

  await settingsStore.update(settings);

  cookies.set("PREF_THEME", settings.site.theme, {
    maxAge: 60 * 60 * 24 * 30,
  });
  cookies.set("PREF_LANG", settings.site.locale, {
    maxAge: 60 * 60 * 24 * 30,
  });

  if (settings.site.theme === "dark") {
    document.documentElement.classList.remove("light");
    document.documentElement.classList.add("dark");
  } else if (settings.site.theme === "light") {
    document.documentElement.classList.remove("dark");
    document.documentElement.classList.add("light");
  } else if (settings.site.theme === "system") {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      document.documentElement.classList.remove("light");
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    }
  }

  setTimeout(() => {
    close();
    isUpdating.value = false;

    if (settings.site.locale !== route.params.locale) {
      const locale = settings.site.locale;
      nuxtApp.$i18n.global.locale.value = locale;
      setDefaultLinkLocale(reverseLocaleMap(locale));
      navigateTo(
        {
          path: `/${reverseLocaleMap(locale)}${route.path.replace(
            `/${route.params.locale}`,
            "",
          )}`,
          query: route.query,
        },
        { replace: true },
      );
    }
  }, 500);

  if (route.query.returnTo) {
    showGoBackToChapterModal.value = true;
  }
}

onMounted(() => {
  settingsStore?.synchronizer.sync().then(() => {
    settingsStore.settings = settingsStore.synchronizer.settings;
    pending.value = false;
  });
});
</script>
